import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useNotification } from './NotificationContext';

interface AdminUser {
  id: string;
  role: 'admin' | 'super_admin';
}

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  session: any | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Function to check admin status and set up user state
  const checkAdminStatus = async (sessionData: any) => {
    try {
      if (!sessionData) {
        setAdminUser(null);
        setSession(null);
        return false;
      }

      // Check if user is admin
      const { data: permissions, error: permError } = await supabase
        .from('user_permissions')
        .select('is_admin')
        .eq('user_id', sessionData.user.id)
        .maybeSingle();

      if (permError || !permissions?.is_admin) {
        throw new Error('Unauthorized: Admin access required');
      }

      // Get admin role
      const { data: adminData, error: adminError } = await supabase
        .rpc('get_admin_role', { user_id_param: sessionData.user.id })
        .single();

      if (adminError) {
        throw new Error('Failed to fetch admin role');
      }

      setAdminUser({
        id: sessionData.user.id,
        role: adminData.role as 'admin' | 'super_admin'
      });
      
      setSession(sessionData);
      return true;
    } catch (error) {
      console.error('Admin status check error:', error);
      setAdminUser(null);
      setSession(null);
      return false;
    }
  };

  // Silent refresh mechanism
  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    const setupRefreshTimer = () => {
      // Refresh session every 30 minutes
      refreshTimer = setInterval(async () => {
        try {
          const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
          
          if (error || !refreshedSession) {
            throw error || new Error('Failed to refresh session');
          }

          await checkAdminStatus(refreshedSession);
        } catch (error) {
          console.error('Session refresh error:', error);
          // If refresh fails, redirect to login
          navigate('/admin/login');
        }
      }, 30 * 60 * 1000); // 30 minutes
    };

    setupRefreshTimer();

    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [navigate]);

  // Initial session check and auth state change listener
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (!initialSession) {
          setAdminUser(null);
          setSession(null);
          navigate('/admin/login');
          return;
        }

        const isAdmin = await checkAdminStatus(initialSession);
        if (!isAdmin) {
          navigate('/admin/login');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setAdminUser(null);
        setSession(null);
        navigate('/admin/login');
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        const isAdmin = await checkAdminStatus(session);
        if (!isAdmin) {
          navigate('/admin/login');
        }
      }
    });

    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      // Validate email format
      if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error('Invalid email format');
      }

      // Validate password (minimum requirements)
      if (password.length < 6) {
        throw new Error('Password should be at least 6 characters long');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        // Map Supabase errors to user-friendly messages
        if (signInError.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password');
        } else if (signInError.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email address');
        }
        throw signInError;
      }

      if (!data.session) {
        throw new Error('No session after sign in');
      }
      
      // Check if user is admin
      const { data: permissions, error: permError } = await supabase
        .from('user_permissions')
        .select('is_admin')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (permError || !permissions?.is_admin) {
        await supabase.auth.signOut();
        throw new Error('Unauthorized: Admin access required');
      }
      
      // Get admin role
      const { data: adminData, error: adminError } = await supabase
        .rpc('get_admin_role', { user_id_param: data.user.id })
        .single();

      if (adminError) {
        await supabase.auth.signOut();
        throw new Error('Failed to fetch admin role');
      }

      setAdminUser({
        id: data.user.id,
        role: adminData.role as 'admin' | 'super_admin'
      });
      
      setSession(data.session);
      showNotification('success', 'Successfully signed in as admin');
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      const message = error instanceof Error ? error.message : 'Invalid credentials';
      showNotification('error', message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setAdminUser(null);
      setSession(null);
      navigate('/admin/login');
      showNotification('success', 'Successfully signed out');
    } catch (error) {
      console.error('Sign out error:', error);
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      showNotification('error', message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminAuthContext.Provider value={{ adminUser, session, loading, signInWithEmail, signOut }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}